import { Utils } from "@crispico/foundation-react";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { MissionItemRendererSettings, XopsInitializationsForClient } from "app";
import Color from 'color';
import _ from "lodash";
import { Color as XopsColor, XopsColorRegistry } from "pages/XopsColor/XopsColorRegistry";
import React from "react";
import { Icon } from "semantic-ui-react";
import { AbstractItemRenderer } from "./AbstractItemRenderer";
import { Mission, Task } from "../../../types";
import { missionEntityDescriptor } from "AppEntityDescriptors";
import { ColorRegistry } from "@crispico/foundation-react/utils/ColorRegistry";

const COLOR_WHITE = Color('white');
const COLOR_GRAY = "#808080";

enum CircleColor {
    NONE = "NONE",
    MISSION_COLOR = "MISSION_COLOR",
    MISSION_STATUS_COLOR = "MISSION_STATUS_COLOR"
}
enum BackgroundColor {
    MISSION_COLOR = "MISSION_COLOR",
    MISSION_STATUS_COLOR = "MISSION_STATUS_COLOR",
    FLIGHT_TYPE_COLOR = "FLIGHT_TYPE_COLOR",
    FLIGHT_COLOR = "FLIGHT_COLOR",
    TASK_TYPE_COLOR = "TASK_TYPE_COLOR",
    FLIGHT_LAST_KNOWN_DATE_TYPE_COLOR = "FLIGHT_LAST_KNOWN_DATE_TYPE_COLOR", // not implemented
    BASED_ON_TASK_STATUS_COLOR = "BASED_ON_TASK_STATUS_COLOR" // not implemented
}

export class MissionItemRenderer extends AbstractItemRenderer<Mission> {

    // shouldComponentUpdate(nextProps: any) {
    //     return !_.isEqual(JSON.stringify(this.propsCasted.entity), JSON.stringify(nextProps.entity));
    // }

    protected get entityName(): string {
        return "Mission2";
    }

    static getMissionStatusColor(mission: Omit<Mission, 'tasks'>, alpha?: number) {
        let color = COLOR_GRAY;
        if (!mission?.status) {
            return color;
        }
        const interval = missionEntityDescriptor.getField("status").getFieldInterval(mission.status);
        return interval?.color ? Utils.convertColorToHex(ColorRegistry.INSTANCE.get(interval.color)) : color;
    }

    static getMissionColor(mission: Omit<Mission, 'tasks'>, alpha?: number) {
        let color = COLOR_GRAY;
        if (!mission?.status) {
            color = XopsColorRegistry.INSTANCE.getColor(XopsColor.NO_MISSION);
        } else if (mission.color) {
            color = mission.color;
        } else if (mission.finishedByUser) {
            color = XopsColorRegistry.INSTANCE.getColor(XopsColor.MISSION_STATUS_FINISHED_BYUSER);
        } else {
            color = MissionItemRenderer.getMissionStatusColor(mission, alpha);
        }
        return Utils.getRGBAColor(color, alpha);
    }

    protected getMissionItemRendererSettings(mission: Mission): MissionItemRendererSettings {
        const { mission2Settings } = (AppMetaTempGlobals.appMetaInstance.helperAppContainer.dispatchers.getState().appContainerContextValue.initializationsForClient as XopsInitializationsForClient);
        const missionItemRenderers: MissionItemRendererSettings[] = mission2Settings.missionItemRenderers ?? [];
        let defaultMissionItemRenderer: MissionItemRendererSettings = {} as MissionItemRendererSettings;

        for (const missionItemRenderer of missionItemRenderers) {
            if (!missionItemRenderer.missionType) {
                defaultMissionItemRenderer = missionItemRenderer;
            }
            if (!mission.type) {
                break;
            } else if (missionItemRenderer.missionType == mission.type.id) {
                defaultMissionItemRenderer = missionItemRenderer;
                break;
            }
        }
        return defaultMissionItemRenderer;
    }

    protected getBackgroundColors(): string[] {
        const mission = this.propsCasted.entity;
        if (!mission) {
            return [];
        }
        if (mission.inactivityType) {
            return [this.getDefaultColor()];
        }
        let colors: string[] = [];
        let { backgroundColor } = this.getMissionItemRendererSettings(mission);
        const tasks = mission?.tasks;
        switch (backgroundColor) {
            case BackgroundColor.MISSION_STATUS_COLOR:
                colors.push(MissionItemRenderer.getMissionStatusColor(mission));
                break;
            // This is necessary because I don't want to duplicate the code for the next case and I get the error "Fallthrough case in switch."
            // @ts-expect-error
            case BackgroundColor.TASK_TYPE_COLOR:
                if (tasks.length && tasks[0].taskType?.color && tasks.length == tasks.filter((task: any) => task.taskType?.id == tasks[0].taskType.id).length) {
                    colors.push(tasks[0].taskType.color);
                    break;
                }
            case BackgroundColor.MISSION_COLOR:
                if (mission.color) {
                    colors.push(mission.color);
                } else {
                    colors.push(MissionItemRenderer.getMissionColor(mission));
                }
                break;
            case BackgroundColor.FLIGHT_TYPE_COLOR:
                let foundDeparture = false;
                let foundArrival = false;
                tasks.forEach((task: any) => {
                    if (task.taskGroup.departure) {
                        foundDeparture = true
                    } else {
                        foundArrival = true;
                    }
                })
                if (foundDeparture) {
                    colors.push(XopsColorRegistry.INSTANCE.getColor(XopsColor.FLIGHT_DEPARTURE));
                }
                if (foundArrival) {
                    colors.push(XopsColorRegistry.INSTANCE.getColor(XopsColor.FLIGHT_ARRIVAL));
                }
                break;
            case BackgroundColor.FLIGHT_COLOR:
                tasks.forEach((task: any) => {
                    colors.push(task.taskGroup.color);
                })
                break;
        }

        if (!colors.length) {
            colors = [this.getDefaultColor()];
        }
        return colors;
    }

    protected getDefaultColor(): string {
        const mission = this.propsCasted.entity;
        if (mission?.inactivityType && mission.inactivityType.color != undefined) {
            return Utils.convertColorToHex(mission.inactivityType.color);
        }
        return mission?.type?.color !== undefined ? mission.type?.color : "grey";
    }

    getBackgroundGradient() {
        let colors = this.getBackgroundColors();

        let offsetBackground: string | undefined = undefined;
        let startOffset = 0;
        const mission = this.propsCasted.entity;
        const tasks = mission?.tasks;
        if (mission && tasks && tasks.length == 1 && tasks[0].actualStartTime && tasks[0].actualStartTime - mission.startTime) {
            let offset = tasks[0].actualStartTime - mission.startTime;
            let duration = mission.endTime - mission.startTime;
            startOffset = offset * 100 / duration;
            const color = MissionItemRenderer.getMissionColor(mission);
            const c = Color(color)
                .mix(COLOR_WHITE, this.getGradientBrightness())
                .hex();
            offsetBackground = `linear-gradient(${c + ' ' + this.getGradientStop() + '%, ' + color}) 0% 0% / ${startOffset}% 100%`
        }

        colors = colors.map((color: any, index: number) => {
            // linear-gradient does not know how to work with color in int format
            if (typeof color === "number") {
                color = Utils.convertColorToHex(color);
            }
            const c = Color(color)
                .mix(COLOR_WHITE, this.getGradientBrightness())
                .hex();
            return `linear-gradient(${c + ' ' + this.getGradientStop() + '%, ' + color}) ${index * 100 / colors.length + startOffset}% 0% / ${(index + 1) * 100 / colors.length}% 100%`
        })
        colors = offsetBackground ? [offsetBackground].concat(colors) : colors;

        return colors.join(",");
    }

    getColor() {
        // The first color (or default) is used to determine the text color based on contrast
        return this.getBackgroundColors()[0] ?? COLOR_GRAY;
    }

    getStyle() {
        const mission = this.propsCasted.entity;
        var style = {
            ...super.getStyle(),
            borderColor: this.getBorderColor(mission),
            backgroundPosition: 'left'
        };
        return style;
    }

    private getBorderColor(mission: Mission) {
        return mission?.type?.color ?? "grey";
    }

    protected renderCircle(): React.ReactNode {
        const mission = this.propsCasted.entity;
        let color;
        let { circleColor } = this.getMissionItemRendererSettings(mission);

        if (mission?.inactivityType || circleColor == CircleColor.NONE) {
            return null;
        }

        switch (circleColor) {
            case CircleColor.MISSION_STATUS_COLOR:
                color = MissionItemRenderer.getMissionStatusColor(mission);
                break
            case CircleColor.MISSION_COLOR:
                color = mission.color;
                break;
            default:
                color = Utils.getRGBAColor(COLOR_GRAY);
        }
        // need to remove the background, because the icon from semantic ui have the background: none and it can override the background from parent
        return <span><Icon name="circle" style={{ color, backgroundColor: undefined, position: 'relative' }} /></span>
    }

    protected renderContent(): string | JSX.Element {
        const mission = this.propsCasted.entity;
        let content: React.ReactNode[] = [];

        if (mission?.inactivityType) {
            content = this.renderFields(mission, ["inactivityType.name"]);
        } else if (mission?.tasks.length) {
            const uniqueFlights = new Set<number>();
            content = mission.tasks.reduce<React.ReactNode[]>((acc, task) => {
                const { taskGroup } = task;
                const { id } = taskGroup;
                if (!uniqueFlights.has(id)) {
                    uniqueFlights.add(id);
                    acc.push(
                        <span className="flex-container-row" key={id}>
                            {entityDescriptors["Flight"].getField("departure").renderField(taskGroup)}
                            {entityDescriptors["Flight"].getField("name").renderField(taskGroup)}
                        </span>
                    );
                }
                return acc;
            }, []);
        } else if (mission?.type?.name) {
            content.push(mission.type.name);
        }
        return (<>
            {this.renderCircle()}
            {content}
        </>);
    }
} 
